// frontend/src/pages/Home.jsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import Calendar from 'react-calendar';
import { Link, useNavigate } from 'react-router-dom';
import api from '../services/api';
import 'react-calendar/dist/Calendar.css';
import '../styles/Home.css';

const Home = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [date, setDate] = useState(new Date());
  const [eventos, setEventos] = useState([]);
  const [actividadReciente, setActividadReciente] = useState([]);
  const [loading, setLoading] = useState(true);
  const [documentosRecientes, setDocumentosRecientes] = useState([]);
  const [activeTab, setActiveTab] = useState('todos');
  const [stats, setStats] = useState({
    documentosPendientes: 0,
    documentosAprobados: 0,
    notificacionesNoLeidas: 0,
    institucionesActivas: 0,
    eventosHoy: 0
  });

  useEffect(() => {
    const cargarTodo = async () => {
      setLoading(true);
      await Promise.all([
        cargarEventos(),
        cargarActividadReciente(),
        cargarEstadisticas(),
        cargarDocumentosRecientes()
      ]);
      setLoading(false);
    };
    
    cargarTodo();
  }, []);

  const cargarEventos = async () => {
    try {
      const response = await api.get('/eventos/proximos');
      if (response.data && response.data.data) {
        setEventos(Array.isArray(response.data.data) ? response.data.data : []);
        setStats(prev => ({ ...prev, eventosHoy: response.data.data.length }));
      }
    } catch (error) {
      console.error('Error al cargar eventos:', error);
    }
  };

  const cargarActividadReciente = async () => {
    try {
      const response = await api.get('/usuarios/perfil/actividad');
      console.log('Actividad reciente:', response.data);
      
      if (response.data && response.data.data) {
        const { documentos, eventos, notificaciones } = response.data.data;
        const items = [];
        
        // Agregar documentos
        documentos.forEach(doc => {
          let estadoTexto = '';
          let estadoColor = '';
          let estadoIcono = '';
          
          switch (doc.estatus) {
            case 'aprobado':
              estadoTexto = 'aprobado';
              estadoColor = '#28a745';
              estadoIcono = '✅';
              break;
            case 'pendiente':
              estadoTexto = 'pendiente';
              estadoColor = '#ffc107';
              estadoIcono = '⏳';
              break;
            case 'en_revision':
              estadoTexto = 'en revisión';
              estadoColor = '#17a2b8';
              estadoIcono = '🔄';
              break;
            case 'rechazado':
              estadoTexto = 'rechazado';
              estadoColor = '#dc3545';
              estadoIcono = '❌';
              break;
            default:
              estadoTexto = doc.estatus;
              estadoColor = '#6c757d';
              estadoIcono = '📄';
          }

            // Obtener el nombre de la institución
            const nombreInstitucion = doc.institucionId?.nombre || 'Una institución';
            
            // Construir el mensaje
            let mensaje = '';
            if (estadoTexto === 'pendiente de revisión' || estadoTexto === 'en revisión') {
              mensaje = `${nombreInstitucion} ha subido un nuevo documento: ${doc.nombre}`;
            } else {
              mensaje = `${nombreInstitucion} - Documento ${estadoTexto}: ${doc.nombre}`;
            }
          
          items.push({
            id: doc._id,
            tipo: 'documento',
            titulo: doc.nombre,
            descripcion: `Documento ${estadoTexto}`,
            fecha: doc.fechaSubida,
            icono: estadoIcono,
            link: '/documentos', // Redirige a la página de documentos
            color: estadoColor
          });
        });
        
        // Agregar eventos
        eventos.forEach(evt => {
          items.push({
            id: evt._id,
            tipo: 'evento',
            titulo: evt.titulo,
            descripcion: `Evento ${evt.tipo} - ${new Date(evt.fecha).toLocaleDateString()}`,
            fecha: evt.fecha,
            icono: '📅',
            link: '/calendario',
            color: '#17a2b8'
          });
        });
        
        // Agregar notificaciones
        notificaciones.forEach(notif => {
          let notifIcono = 'ℹ️';
          if (notif.tipo === 'exito') notifIcono = '✅';
          if (notif.tipo === 'advertencia') notifIcono = '⚠️';
          if (notif.tipo === 'error') notifIcono = '❌';
          
          items.push({
            id: notif._id,
            tipo: 'notificacion',
            titulo: notif.titulo,
            descripcion: notif.mensaje,
            fecha: notif.fecha,
            icono: notifIcono,
            leida: notif.leida,
            link: '#',
            color: notif.leida ? '#6c757d' : '#a81544'
          });
        });
        
        items.sort((a, b) => new Date(b.fecha) - new Date(a.fecha));
        setActividadReciente(items.slice(0, 10));
      }
    } catch (error) {
      console.error('Error al cargar actividad reciente:', error);
      setActividadReciente([]);
    }
  };

  const cargarEstadisticas = async () => {
    try {
      if (user?.role?.includes('admin')) {
        const [docsRes, instRes] = await Promise.all([
          api.get('/documentos'),
          api.get('/instituciones')
        ]);
        
        let documentosData = [];
        if (docsRes.data && docsRes.data.data) {
          documentosData = docsRes.data.data;
        }
        
        setStats(prev => ({
          ...prev,
          documentosPendientes: documentosData.filter(d => d.estatus === 'pendiente').length,
          documentosAprobados: documentosData.filter(d => d.estatus === 'aprobado').length,
          institucionesActivas: instRes.data?.data?.length || 0
        }));
      } else if (user?.role === 'institucion') {
        const response = await api.get('/documentos');
        let documentosData = [];
        if (response.data && response.data.data) {
          documentosData = response.data.data;
        }
        
        setStats(prev => ({
          ...prev,
          documentosPendientes: documentosData.filter(d => d.estatus === 'pendiente').length,
          documentosAprobados: documentosData.filter(d => d.estatus === 'aprobado').length
        }));
      }
    } catch (error) {
      console.error('Error al cargar estadísticas:', error);
    }
  };

  const cargarDocumentosRecientes = async () => {
    try {
      const response = await api.get('/documentos?limit=5');
      if (response.data && response.data.data) {
        setDocumentosRecientes(Array.isArray(response.data.data) ? response.data.data : []);
      }
    } catch (error) {
      console.error('Error al cargar documentos recientes:', error);
    }
  };

  const getEventosDelDia = () => {
    const hoy = new Date().toDateString();
    return eventos.filter(e => {
      try {
        return e && e.fecha && new Date(e.fecha).toDateString() === hoy;
      } catch {
        return false;
      }
    });
  };

  const getSaludo = () => {
    const hora = new Date().getHours();
    if (hora < 12) return '¡Buenos días!';
    if (hora < 18) return '¡Buenas tardes!';
    return '¡Buenas noches!';
  };

  const getRoleName = (role) => {
    const roles = {
      'super_admin_jasp': 'Super Administrador',
      'admin_jasp': 'Administrador',
      'consultor_1': 'Consultor',
      'consultor_2': 'Consultor',
      'consultor_3': 'Consultor',
      'institucion': 'Institución'
    };
    return roles[role] || role;
  };

  const getTipoIcono = (tipo) => {
    const iconos = {
      documento: '📄',
      evento: '📅',
      notificacion: '🔔'
    };
    return iconos[tipo] || '📌';
  };

  const handleActividadClick = (item) => {
    if (item.link && item.link !== '#') {
      navigate(item.link);
    }
  };

  if (loading) {
    return (
      <div className="home-loading">
        <div className="loading-spinner"></div>
        <p>Cargando tu dashboard...</p>
      </div>
    );
  }

  const eventosHoy = getEventosDelDia();

  const actividadFiltrada = actividadReciente.filter(item => {
    if (activeTab === 'todos') return true;
    if (activeTab === 'documentos') return item.tipo === 'documento';
    if (activeTab === 'eventos') return item.tipo === 'evento';
    if (activeTab === 'notificaciones') return item.tipo === 'notificacion';
    return true;
  });

  return (
    <div className="home">
      {/* Header */}
      <div className="home-header">
        <div className="header-left">
          <h1>{getSaludo()}</h1>
          <p className="fecha-actual">
            {new Date().toLocaleDateString('es-MX', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </p>
        </div>
        <div className="header-right">
          <div className="user-profile-mini">
            <div className="user-avatar">
              {user?.nombre?.charAt(0) || 'U'}
            </div>
            <div className="user-info-mini">
              <span className="user-name">{user?.nombre}</span>
              <span className="user-role">{getRoleName(user?.role)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Tarjetas de estadísticas */}
      <div className="stats-grid-moderno">
        {user?.role?.includes('admin') ? (
          <>
            <div className="stat-card-moderno">
              <div className="stat-icon-wrapper documentos">
                <span className="stat-icon">📄</span>
              </div>
              <div className="stat-content">
                <h3>{stats.documentosPendientes}</h3>
                <p>Documentos pendientes</p>
                <Link to="/documentos?estatus=pendiente" className="stat-link">
                  Ver todos →
                </Link>
              </div>
            </div>

            <div className="stat-card-moderno">
              <div className="stat-icon-wrapper aprobados">
                <span className="stat-icon">✅</span>
              </div>
              <div className="stat-content">
                <h3>{stats.documentosAprobados}</h3>
                <p>Documentos aprobados</p>
                <Link to="/documentos?estatus=aprobado" className="stat-link">
                  Ver todos →
                </Link>
              </div>
            </div>

            <div className="stat-card-moderno">
              <div className="stat-icon-wrapper instituciones">
                <span className="stat-icon">🏢</span>
              </div>
              <div className="stat-content">
                <h3>{stats.institucionesActivas}</h3>
                <p>Instituciones activas</p>
                <Link to="/admin/instituciones" className="stat-link">
                  Gestionar →
                </Link>
              </div>
            </div>

            <div className="stat-card-moderno">
              <div className="stat-icon-wrapper eventos">
                <span className="stat-icon">📅</span>
              </div>
              <div className="stat-content">
                <h3>{stats.eventosHoy}</h3>
                <p>Eventos</p>
                <Link to="/calendario" className="stat-link">
                  Ver calendario →
                </Link>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="stat-card-moderno">
              <div className="stat-icon-wrapper documentos">
                <span className="stat-icon">📄</span>
              </div>
              <div className="stat-content">
                <h3>{stats.documentosPendientes}</h3>
                <p>Documentos pendientes</p>
                <Link to="/documentos?estatus=pendiente" className="stat-link">
                  Revisar →
                </Link>
              </div>
            </div>

            <div className="stat-card-moderno">
              <div className="stat-icon-wrapper aprobados">
                <span className="stat-icon">✅</span>
              </div>
              <div className="stat-content">
                <h3>{stats.documentosAprobados}</h3>
                <p>Documentos aprobados</p>
                <Link to="/documentos?estatus=aprobado" className="stat-link">
                  Ver todos →
                </Link>
              </div>
            </div>

            <div className="stat-card-moderno">
              <div className="stat-icon-wrapper formatos">
                <span className="stat-icon">📋</span>
              </div>
              <div className="stat-content">
                <h3>Formatos</h3>
                <p>Descarga plantillas</p>
                <Link to="/formatos" className="stat-link">
                  Ver formatos →
                </Link>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Grid principal */}
      <div className="dashboard-grid">
        {/* Columna izquierda - Calendario */}
        <div className="dashboard-col">
          <div className="card-moderno calendario-card">
            <div className="card-header">
              <h2>
                <span className="card-icon">📅</span>
                Calendario
              </h2>
              <Link to="/calendario" className="card-link">Ver completo</Link>
            </div>
            
            <Calendar
              onChange={setDate}
              value={date}
              tileContent={({ date }) => {
                const tieneEventos = eventos.some(e => {
                  try {
                    return e.fecha && new Date(e.fecha).toDateString() === date.toDateString();
                  } catch {
                    return false;
                  }
                });
                return tieneEventos ? <span className="event-dot"></span> : null;
              }}
            />

            {eventosHoy.length > 0 && (
              <div className="eventos-hoy-mini">
                <h3>
                  <span className="event-icon">⏰</span>
                  Eventos
                </h3>
                {eventosHoy.slice(0, 3).map(evento => (
                  <div key={evento._id} className="evento-mini">
                    <span className={`evento-tipo-mini ${evento.tipo}`}></span>
                    <div className="evento-info-mini">
                      <span className="evento-titulo">{evento.titulo}</span>
                      <span className="evento-hora">
                        {new Date(evento.fecha).toLocaleTimeString('es-MX', {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Columna derecha - Actividad Reciente */}
        <div className="dashboard-col">
          <div className="card-moderno actividad-card">
            <div className="card-header">
              <h2>
                <span className="card-icon">📋</span>
                Actividad Reciente
              </h2>
              <Link to="/perfil?tab=actividad" className="card-link"></Link>
            </div>

            {/* Pestañas de filtro */}
            <div className="actividad-tabs-home">
              <button 
                className={`tab-btn ${activeTab === 'todos' ? 'active' : ''}`}
                onClick={() => setActiveTab('todos')}
              >
                Todos
              </button>
              <button 
                className={`tab-btn ${activeTab === 'documentos' ? 'active' : ''}`}
                onClick={() => setActiveTab('documentos')}
              >
                Documentos
              </button>
              <button 
                className={`tab-btn ${activeTab === 'eventos' ? 'active' : ''}`}
                onClick={() => setActiveTab('eventos')}
              >
                Eventos
              </button>
              <button 
                className={`tab-btn ${activeTab === 'notificaciones' ? 'active' : ''}`}
                onClick={() => setActiveTab('notificaciones')}
              >
                Notificaciones
              </button>
            </div>

            <div className="actividad-lista-home">
              {actividadFiltrada.length === 0 ? (
                <div className="empty-state">
                  <span className="empty-icon">📭</span>
                  <p>No hay actividad reciente</p>
                </div>
              ) : (
                actividadFiltrada.map(item => (
                  <div 
                    key={`${item.tipo}-${item.id}`} 
                    className="actividad-item-home"
                    style={{ borderLeftColor: item.color }}
                    onClick={() => handleActividadClick(item)}
                  >
                    <div className="actividad-icon-home">
                      {getTipoIcono(item.tipo)}
                    </div>
                    <div className="actividad-contenido-home">
                      <div className="actividad-titulo-home">
                        <h4>{item.titulo}</h4>
                        <span className="actividad-fecha-home">
                          {new Date(item.fecha).toLocaleDateString()}
                        </span>
                      </div>
                      <p className="actividad-descripcion-home">{item.descripcion}</p>
                    </div>
                    <div className="actividad-badge-home">
                      {item.tipo === 'documento' && (
                        <span className={`badge ${item.descripcion.includes('aprobado') ? 'success' : 
                          item.descripcion.includes('pendiente') ? 'warning' : 
                          item.descripcion.includes('revisión') ? 'info' : 'danger'}`}>
                          {item.icono}
                        </span>
                      )}
                      {item.tipo === 'evento' && (
                        <span className="badge info">📅</span>
                      )}
                      {item.tipo === 'notificacion' && !item.leida && (
                        <span className="badge nuevo">Nuevo</span>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;